import { mergeAttributes, Node } from "@tiptap/core";
import type { MarkdownToken } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { Parser } from "htmlparser2";

import { REPORT_BLOCK_TAGS } from "src/components/llm/markdown/report-block-tags";
import { REPORT_BLOCK_SELECTOR } from "src/components/markdown-editor/report-block-scope";
import { ReportBlockNodeView } from "src/components/markdown-editor/report-block-view";

/** ProseMirror node name for a report tag block kept verbatim. */
const REPORT_BLOCK_NAME = "reportBlock";

/** Marked token name emitted by the report-block tokenizer. */
const REPORT_BLOCK_TOKEN = "reportBlock";

const TAG_NAMES = REPORT_BLOCK_TAGS.join("|");

// An allowlisted tag opening a line, indented like any markdown block (at most 3 spaces).
const OPENING_LINE = new RegExp(`^ {0,3}<(${TAG_NAMES})(?=[\\s/>])`);

/** An allowlisted opening tag at the start of `src` through its matching close, counting same-name nesting; anything that doesn't close cleanly is left to the normal parse. */
function matchReportBlock(
  src: string,
): { raw: string; source: string } | undefined {
  const tag = OPENING_LINE.exec(src)?.[1];
  if (tag === undefined) return undefined;
  const end = matchingCloseEnd(src, tag);
  if (end === undefined) return undefined;
  const lineEnd = /^[ \t]*(?:\n|$)/.exec(src.slice(end));
  if (!lineEnd) return undefined;
  return {
    raw: src.slice(0, end + lineEnd[0].length),
    source: src.slice(0, end),
  };
}

/** Where the tag opening `src` is closed, read with an HTML tokenizer so comments, quoted attributes and raw text such as `<style>` are skipped. */
function matchingCloseEnd(src: string, tag: string): number | undefined {
  let depth = 0;
  let isSelfClosing = false;
  let end: number | null | undefined;
  const parser = new Parser(
    {
      onopentag(name) {
        if (end !== undefined || name !== tag) return;
        if (depth === 0) {
          isSelfClosing = src
            .slice(parser.startIndex, parser.endIndex + 1)
            .endsWith("/>");
        }
        depth += 1;
      },
      onclosetag(name, isImplied) {
        if (end !== undefined || name !== tag) return;
        depth -= 1;
        if (depth > 0) return;
        // The parser also closes tags left open at the end of the input; only a self-closing opening tag counts as closed.
        end = !isImplied || isSelfClosing ? parser.endIndex + 1 : null;
      },
    },
    { recognizeSelfClosing: true, decodeEntities: false },
  );
  parser.end(src);
  return end ?? undefined;
}

/** Agent report markup kept as its source and written back byte-identical; without it the editor saves the markup escaped as paragraph text. */
export const ReportBlock = Node.create({
  name: REPORT_BLOCK_NAME,
  group: "block",
  atom: true,
  // A React node view drag duplicates the node instead of moving it; cut and paste moves it.
  draggable: false,

  addAttributes() {
    return {
      source: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-source") ?? "",
        renderHTML: (attributes) => ({ "data-source": attributes.source }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `div${REPORT_BLOCK_SELECTOR}` }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-report-block": "" }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ReportBlockNodeView);
  },

  markdownTokenName: REPORT_BLOCK_TOKEN,
  markdownTokenizer: {
    name: REPORT_BLOCK_TOKEN,
    level: "block",
    // A report tag never interrupts a paragraph (as in CommonMark), so a block only starts after a blank line or another block.
    start: () => -1,
    tokenize(src: string) {
      const matched = matchReportBlock(src);
      if (!matched) return undefined;
      return {
        type: REPORT_BLOCK_TOKEN,
        raw: matched.raw,
        source: matched.source,
      };
    },
  },

  parseMarkdown(token: MarkdownToken) {
    return {
      type: REPORT_BLOCK_NAME,
      attrs: { source: token.source },
    };
  },

  // Plain-text copies keep the block's markup instead of dropping it.
  renderText({ node }) {
    return node.attrs.source;
  },

  renderMarkdown(node) {
    return node.attrs?.source ?? "";
  },
});
